import { PROTOCOL_VERSION } from './protocol-version.js';
import { isWeather } from './types.js';
import type { MapDefinition, TrackPoint } from './types.js';

export const MAP_CACHE_SCHEMA_VERSION = 2;
export const MAP_CACHE_KEY = `nightshift:maps:v${MAP_CACHE_SCHEMA_VERSION}`;
export const LEGACY_MAP_CACHE_KEY = 'nightshift:maps:v1';

const HEX = /^#[0-9a-f]{6}$/i;
const TRACKSIDE_EVENT_KINDS = new Set(['festival', 'service', 'market', 'construction', 'lookout', 'industrial']);
const TRACKSIDE_EVENT_EFFECTS = new Set(['nitro-zone', 'repair-zone', 'precision-zone']);
const CITY_SETPIECES = new Set([
  'bern-tram',
  'luzern-drawbridge',
  'hannover-roadworks',
  'berlin-convoy',
]);
const ROAD_SECTION_KINDS = new Set(['launch', 'technical', 'hero', 'sprint']);
const ROAD_STYLES = new Set([
  'boulevard', 'mountain', 'village', 'shore', 'bridge', 'expressway',
  'urban', 'industrial', 'canyon', 'quarry', 'desert', 'jungle', 'lunar',
]);
const LANDMARK_KINDS = new Set([
  'ski-station', 'lighthouse', 'cable-car', 'neon-tower', 'quarry', 'observatory',
  'zytglogge', 'federal-palace', 'chapel-bridge', 'new-town-hall', 'brandenburg-gate', 'tv-tower',
  'jungle-temple', 'moon-base',
]);
const ROUTE_SURFACES = new Set(['asphalt', 'service', 'gravel']);
const ROUTE_RISKS = new Set(['safe', 'technical', 'extreme']);

export interface StoredMapCache {
  version: typeof MAP_CACHE_SCHEMA_VERSION;
  protocolVersion: number;
  savedAt: number;
  maps: MapDefinition[];
}

export function createMapCache(maps: MapDefinition[], savedAt = Date.now()): StoredMapCache {
  return {
    version: MAP_CACHE_SCHEMA_VERSION,
    protocolVersion: PROTOCOL_VERSION,
    savedAt,
    maps,
  };
}

export function parseMapCache(value: unknown, now = Date.now()): MapDefinition[] | null {
  if (
    !isRecord(value)
    || value.version !== MAP_CACHE_SCHEMA_VERSION
    || value.protocolVersion !== PROTOCOL_VERSION
    || !finite(value.savedAt)
    || Number(value.savedAt) > now + 60_000
  ) return null;
  return validateMapSet(value.maps);
}

/** Browser-safe structural validation for network and LocalStorage map data.
 * The server owns the stricter authoring ranges; this boundary guarantees that
 * every field consumed by rendering and physics exists and is finite. */
export function validateMapSet(value: unknown): MapDefinition[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > 64) return null;
  const ids = new Set<string>();
  for (const candidate of value) {
    if (!validMap(candidate) || ids.has(candidate.id as string)) return null;
    ids.add(candidate.id as string);
  }
  return value as MapDefinition[];
}

function validMap(value: unknown): value is MapDefinition {
  if (!isRecord(value)) return false;
  if (
    !id(value.id)
    || !text(value.name)
    || !text(value.location)
    || !text(value.tagline)
    || !['alpine', 'coastal', 'desert', 'urban', 'forest', 'jungle', 'lunar'].includes(String(value.biome))
    || !['dawn', 'day', 'sunset', 'night'].includes(String(value.timeOfDay))
    || !isWeather(value.weather)
    || !integer(value.laps, 1, 12)
    || !numberInRange(value.roadWidth, 8, 30)
    || !optionalNumber(value.gravity, 0.25, 1.5)
    || !Array.isArray(value.track)
    || value.track.length < 8
    || value.track.length > 512
    || !value.track.every(validTrackPoint)
    || !validPalette(value.palette)
    || !validScenery(value.scenery)
    || !validFeatures(value.features, value.track.length)
  ) return false;
  return true;
}

function validTrackPoint(value: unknown): value is TrackPoint {
  return isRecord(value)
    && numberInRange(value.x, -5_000, 5_000)
    && numberInRange(value.z, -5_000, 5_000)
    && optionalNumber(value.y, -100, 500)
    && optionalNumber(value.width, 8, 30)
    && optionalNumber(value.bank, -12, 12);
}

function validPalette(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return ['skyTop', 'skyHorizon', 'fog', 'ground', 'road', 'shoulder', 'lane', 'accent']
    .every((key) => colour(value[key]));
}

function validScenery(value: unknown): boolean {
  return isRecord(value)
    && numberInRange(value.trees, 0, 2_000)
    && numberInRange(value.rocks, 0, 2_000)
    && numberInRange(value.buildings, 0, 2_000)
    && typeof value.lamps === 'boolean'
    && typeof value.barriers === 'boolean';
}

function validFeatures(value: unknown, trackLength: number): boolean {
  if (
    !isRecord(value)
    || !numberInRange(value.mountains, 0, 100)
    || !validFeatureArray(value.ramps, (entry) => (
      trackIndex(entry.trackIndex, trackLength)
      && numberInRange(entry.length, 4, 30)
      && numberInRange(entry.width, 4, 24)
      && numberInRange(entry.height, 0.5, 8)
    ))
    || !validFeatureArray(value.tunnels, (entry) => (
      trackIndex(entry.trackIndex, trackLength)
      && numberInRange(entry.length, 8, 120)
      && numberInRange(entry.width, 10, 40)
      && numberInRange(entry.height, 4, 16)
    ))
    || !validFeatureArray(value.riskGates, (entry) => (
      trackIndex(entry.trackIndex, trackLength)
      && ['left', 'right'].includes(String(entry.side))
      && numberInRange(entry.offset, 2, 12)
      && numberInRange(entry.width, 2, 8)
      && numberInRange(entry.reward, 4, 25)
    ))
  ) return false;
  return validAlternateRoutes(value.alternateRoutes, trackLength)
    && validLakes(value.lakes)
    && optionalFeatureArray(value.bridges, (entry) => (
      trackIndex(entry.trackIndex, trackLength)
      && numberInRange(entry.length, 10, 180)
      && numberInRange(entry.width, 8, 60)
      && ['stone', 'steel', 'wood'].includes(String(entry.style))
    ))
    && validStuntLoops(value.stuntLoops, trackLength)
    && validTracksideEvents(value.tracksideEvents, trackLength)
    && validRoadSections(value.roadSections, trackLength)
    && validLandmarks(value.landmarks, trackLength);
}

function validAlternateRoutes(value: unknown, trackLength: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 64) return false;
  const ids = new Set<string>();
  return value.every((candidate) => {
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    const from = Number(candidate.fromTrackIndex);
    const to = Number(candidate.toTrackIndex);
    const span = (to - from + trackLength) % trackLength;
    return text(candidate.name, 64)
      && ['shortcut', 'loop'].includes(String(candidate.kind))
      && optionalSetMember(candidate.surface, ROUTE_SURFACES)
      && optionalSetMember(candidate.risk, ROUTE_RISKS)
      && optionalNumber(candidate.reward, 0, 25)
      && trackIndex(candidate.fromTrackIndex, trackLength)
      && trackIndex(candidate.toTrackIndex, trackLength)
      && span >= 2
      && span < trackLength - 1
      && numberInRange(candidate.width, 5, 20)
      && Array.isArray(candidate.points)
      && candidate.points.length >= 1
      && candidate.points.length <= 32
      && candidate.points.every(validRoutePoint);
  });
}

function validRoutePoint(value: unknown): boolean {
  return isRecord(value)
    && numberInRange(value.x, -5_000, 5_000)
    && numberInRange(value.z, -5_000, 5_000)
    && optionalNumber(value.y, -5, 50)
    && optionalNumber(value.width, 8, 30)
    && optionalNumber(value.bank, -12, 12);
}

function validLakes(value: unknown): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 64) return false;
  const ids = new Set<string>();
  return value.every((candidate) => {
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    return numberInRange(candidate.x, -5_000, 5_000)
      && numberInRange(candidate.z, -5_000, 5_000)
      && numberInRange(candidate.radiusX, 8, 500)
      && numberInRange(candidate.radiusZ, 8, 500)
      && optionalNumber(candidate.rotation, -Math.PI * 2, Math.PI * 2)
      && optionalColour(candidate.colour);
  });
}

function validStuntLoops(value: unknown, trackLength: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 64) return false;
  const ids = new Set<string>();
  return value.every((candidate) => {
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    return trackIndex(candidate.trackIndex, trackLength)
      && numberInRange(candidate.radius, 7, 18)
      && numberInRange(candidate.width, 6, 18)
      && numberInRange(candidate.minSpeed, 10, 45);
  });
}

function validTracksideEvents(value: unknown, trackLength: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 64) return false;
  const ids = new Set<string>();
  return value.every((candidate) => {
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    return TRACKSIDE_EVENT_KINDS.has(String(candidate.kind))
      && trackIndex(candidate.trackIndex, trackLength)
      && ['left', 'right'].includes(String(candidate.side))
      && optionalNumber(candidate.scale, 0.7, 1.5)
      && optionalColour(candidate.colour)
      && optionalSetMember(candidate.effect, TRACKSIDE_EVENT_EFFECTS)
      && optionalSetMember(candidate.setpiece, CITY_SETPIECES);
  });
}

function validRoadSections(value: unknown, trackLength: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length !== 0 && value.length !== 4) return false;
  if (value.length === 0) return true;
  const ids = new Set<string>();
  const kinds = new Set<string>();
  let totalSpan = 0;
  for (let index = 0; index < value.length; index += 1) {
    const candidate = value[index];
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    const kind = String(candidate.kind);
    if (!ROAD_SECTION_KINDS.has(kind) || kinds.has(kind)) return false;
    kinds.add(kind);
    if (
      !text(candidate.name, 64)
      || !ROAD_STYLES.has(String(candidate.style))
      || !trackIndex(candidate.fromTrackIndex, trackLength)
      || !trackIndex(candidate.toTrackIndex, trackLength)
      || !optionalColour(candidate.accent)
    ) return false;
    const from = Number(candidate.fromTrackIndex);
    const to = Number(candidate.toTrackIndex);
    const span = (to - from + trackLength) % trackLength;
    if (span < 2) return false;
    totalSpan += span;
    const next = value[(index + 1) % value.length];
    if (!isRecord(next) || candidate.toTrackIndex !== next.fromTrackIndex) return false;
  }
  return totalSpan === trackLength;
}

function validLandmarks(value: unknown, trackLength: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 64) return false;
  const ids = new Set<string>();
  return value.every((candidate) => {
    if (!isRecord(candidate) || !id(candidate.id) || ids.has(candidate.id as string)) return false;
    ids.add(candidate.id as string);
    return text(candidate.name, 64)
      && LANDMARK_KINDS.has(String(candidate.kind))
      && trackIndex(candidate.trackIndex, trackLength)
      && ['left', 'right'].includes(String(candidate.side))
      && numberInRange(candidate.offset, 1, 48)
      && optionalNumber(candidate.scale, 0.65, 1.5)
      && optionalColour(candidate.colour);
  });
}

function validFeatureArray(
  value: unknown,
  validate: (entry: Record<string, unknown>) => boolean,
): boolean {
  return Array.isArray(value) && value.every((entry) => isRecord(entry) && validate(entry));
}

function optionalFeatureArray(
  value: unknown,
  validate: (entry: Record<string, unknown>) => boolean,
): boolean {
  return value === undefined || validFeatureArray(value, validate);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finite(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

function integer(value: unknown, minimum: number, maximum: number): boolean {
  return finite(value) && Number.isInteger(value) && Number(value) >= minimum && Number(value) <= maximum;
}

function numberInRange(value: unknown, minimum: number, maximum: number): boolean {
  return finite(value) && Number(value) >= minimum && Number(value) <= maximum;
}

function optionalNumber(value: unknown, minimum: number, maximum: number): boolean {
  return value === undefined || numberInRange(value, minimum, maximum);
}

function optionalSetMember(value: unknown, values: ReadonlySet<string>): boolean {
  return value === undefined || values.has(String(value));
}

function colour(value: unknown): boolean {
  return typeof value === 'string' && HEX.test(value);
}

function optionalColour(value: unknown): boolean {
  return value === undefined || colour(value);
}

function trackIndex(value: unknown, length: number): boolean {
  return integer(value, 0, Math.max(0, length - 1));
}

function id(value: unknown): boolean {
  return typeof value === 'string' && /^[a-z0-9-]+$/.test(value);
}

function text(value: unknown, maximumLength = Number.POSITIVE_INFINITY): boolean {
  return typeof value === 'string'
    && value.trim().length > 0
    && value.length <= maximumLength;
}
